import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Award, CheckCircle2, XCircle, Clock3, ShieldAlert } from "lucide-react";

const fmtWait = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "now";
  const h = Math.floor(ms / 36e5), m = Math.ceil((ms % 36e5) / 6e4) % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function RMTrainingQuiz({ mode = "module" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isFinal = mode === "final";
  const base = isFinal ? "/rm/training/final" : `/rm/training/modules/${id}/quiz`;

  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(base)
      .then((r) => setQuiz(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Couldn't load the quiz."));
  }, [base]);

  const allAnswered = useMemo(
    () => quiz && quiz.questions.every((_, i) => answers[i] != null),
    [quiz, answers]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(base, {
        answers: quiz.questions.map((_, i) => answers[i]) });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit — try again");
    }
    setBusy(false);
  };

  const heading = isFinal ? "Final Certification Assessment" : `Module ${id} Quiz`;

  return (
    <div className="min-h-screen bg-[#F4F8F8]" data-testid="rm-quiz-page">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <Link to={isFinal ? "/rm/training" : `/rm/training/module/${id}`}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-[#0A8B85]"
            data-testid="rm-quiz-back-btn">
            <ArrowLeft size={14} /> {isFinal ? "All modules" : "Back to module"}
          </Link>
          {quiz && !result && (
            <p className="text-xs font-semibold text-slate-400">
              Pass mark: {quiz.pass_pct}% · {quiz.questions.length} questions
            </p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        {error ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="rm-quiz-error">
            <p className="text-sm font-semibold text-amber-800">{error}</p>
            <Link to="/rm/training" className="mt-3 inline-block rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white">Back to training</Link>
          </div>
        ) : !quiz ? (
          <p className="py-12 text-center text-slate-400">Loading…</p>
        ) : result ? (
          <ResultCard result={result} quiz={quiz} isFinal={isFinal} navigate={navigate} />
        ) : quiz.passed ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="rm-quiz-already-passed">
            <CheckCircle2 className="mx-auto text-emerald-500" size={36} />
            <p className="mt-2 text-lg font-extrabold text-emerald-800">Already passed</p>
            <Link to="/rm/training" className="mt-4 inline-block rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white">Back to training</Link>
          </div>
        ) : quiz.blocked ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="rm-quiz-blocked">
            <ShieldAlert className="mx-auto text-red-500" size={36} />
            <p className="mt-2 text-lg font-extrabold text-red-800">Attempts exhausted</p>
            <p className="mt-1 text-sm text-red-700">Two failed attempts — this has been flagged to the Clinq team. They'll be in touch about next steps.</p>
            <Link to="/rm/training" className="mt-4 inline-block rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white">Back to training</Link>
          </div>
        ) : quiz.next_attempt_at ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="rm-quiz-wait">
            <Clock3 className="mx-auto text-amber-500" size={36} />
            <p className="mt-2 text-lg font-extrabold text-amber-800">Retry not available yet</p>
            <p className="mt-1 text-sm text-amber-700">
              You can retake this quiz in <b>{fmtWait(quiz.next_attempt_at)}</b> — use the time to re-read the module.
            </p>
            <Link to="/rm/training" className="mt-4 inline-block rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white">Back to training</Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A8B85]">{isFinal ? "Clinq Relationship Manager Certification" : quiz.title}</p>
            <h1 className="mt-1 text-2xl font-extrabold text-[#0A2540]" data-testid="rm-quiz-title">{heading}</h1>
            <p className="mt-2 text-sm text-slate-500">
              {quiz.questions.length} multiple-choice questions · pass mark {quiz.pass_pct}%
              {quiz.attempts_used > 0 && <span className="ml-2 font-semibold text-amber-600">Attempt {quiz.attempts_used + 1} of 2</span>}
            </p>

            <div className="mt-6 space-y-7" data-testid="rm-quiz-questions">
              {quiz.questions.map((q, i) => (
                <fieldset key={i} data-testid={`rm-quiz-question-${i}`}>
                  <legend className="text-sm font-bold text-[#0A2540]">
                    <span className="mr-1.5 text-[#0A8B85]">{i + 1}.</span>{q.q}
                  </legend>
                  <div className="mt-2 space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <label key={oi}
                        className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-sm transition ${
                          answers[i] === oi ? "border-[#0A8B85] bg-[#0A8B85]/5" : "border-slate-200 hover:border-slate-300"}`}>
                        <input type="radio" name={`q${i}`} checked={answers[i] === oi}
                          onChange={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                          className="mt-0.5 accent-[#0A8B85]"
                          data-testid={`rm-quiz-q${i}-opt${oi}`} />
                        <span className="text-slate-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>

            <div className="mt-8 border-t border-slate-100 pt-6 text-center">
              <button onClick={submit} disabled={!allAnswered || busy}
                className="rounded-xl bg-[#0A8B85] px-8 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#087a74] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="rm-quiz-submit-btn">
                {busy ? "Grading…" : "Submit answers"}
              </button>
              {!allAnswered && <p className="mt-2 text-[11px] text-slate-400">Answer every question to submit.</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ResultCard({ result, quiz, isFinal, navigate }) {
  const wrongSet = new Set(result.wrong_indexes);
  if (result.passed) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center" data-testid="rm-quiz-result-pass">
        {isFinal ? <Award className="mx-auto text-[#0A8B85]" size={44} /> : <CheckCircle2 className="mx-auto text-emerald-500" size={44} />}
        <p className="mt-3 text-2xl font-extrabold text-[#0A2540]">
          {isFinal ? "Certified!" : "Passed!"} <span className="text-[#0A8B85]">{result.score_pct}%</span>
        </p>
        {isFinal && result.certification ? (
          <div className="mx-auto mt-5 max-w-md rounded-2xl border-2 border-[#0A8B85] bg-[#F4F8F8] p-6" data-testid="rm-certification-card">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">Clinq Relationship Manager Certification</p>
            <p className="mt-2 text-xl font-extrabold text-[#0A2540]">{result.certification.rm_name}</p>
            <p className="mt-1 text-xs text-slate-500">
              Certified {result.certification.certified_at.slice(0, 10)} · Overall score {result.certification.score_pct}%
            </p>
          </div>
        ) : (
          <p className="mt-1 text-sm text-slate-500">Module completed — the next module is now unlocked.</p>
        )}
        <div className="mt-6 flex justify-center gap-2">
          {!isFinal && result.next_module_id && (
            <button onClick={() => navigate(`/rm/training/module/${result.next_module_id}`)}
              className="rounded-xl bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#087a74]"
              data-testid="rm-quiz-next-module-btn">
              Start Module {result.next_module_id}
            </button>
          )}
          {!isFinal && result.all_modules_completed && (
            <button onClick={() => navigate("/rm/training/final")}
              className="rounded-xl bg-[#0A2540] px-5 py-2.5 text-sm font-bold text-white"
              data-testid="rm-quiz-goto-final-btn">
              Start Final Certification
            </button>
          )}
          <Link to="/rm/training" className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">
            All modules
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="rm-quiz-result-fail">
      <div className="rounded-2xl border border-red-200 bg-white p-8 text-center">
        <XCircle className="mx-auto text-red-400" size={44} />
        <p className="mt-3 text-2xl font-extrabold text-[#0A2540]">
          Not this time — <span className="text-red-500">{result.score_pct}%</span>
        </p>
        <p className="mt-1 text-sm text-slate-500">You need {result.pass_pct}% to pass. The questions you missed are highlighted below.</p>
        {result.blocked ? (
          <p className="mx-auto mt-3 max-w-md rounded-lg bg-red-50 px-4 py-2 text-xs font-semibold text-red-700" data-testid="rm-quiz-fail-blocked">
            That was your second attempt — this has been flagged to the Clinq team, who will reach out about next steps.
          </p>
        ) : (
          <p className="mx-auto mt-3 max-w-md rounded-lg bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700" data-testid="rm-quiz-fail-retry-info">
            You can retry once, after a 24-hour waiting period. Re-read the module before your retry.
          </p>
        )}
      </div>
      <div className="mt-5 space-y-2">
        {quiz.questions.map((q, i) => (
          <div key={i}
            className={`rounded-xl border px-4 py-3 text-sm ${wrongSet.has(i)
              ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50/50 text-slate-600"}`}
            data-testid={`rm-quiz-review-q${i}`}>
            <span className="mr-1.5 font-bold">{i + 1}.</span>{q.q}
            <span className={`ml-2 text-[11px] font-bold ${wrongSet.has(i) ? "text-red-500" : "text-emerald-600"}`}>
              {wrongSet.has(i) ? "✗ incorrect" : "✓ correct"}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-5 text-center">
        <Link to="/rm/training" className="rounded-xl bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white">Back to training</Link>
      </div>
    </div>
  );
}
